<template>
    <section class="relative overflow-hidden bg-background-dark py-20 text-white lg:py-28" :id="id">
        <div class="pointer-events-none absolute inset-0" aria-hidden="true">
            <!-- radial-gradient glows, not filter:blur() — large blur overflows the iOS WebKit texture limit and paints white blocks -->
            <div class="absolute -left-20 top-10 h-96 w-96" style="background: radial-gradient(closest-side, rgba(255, 174, 0, 0.20), transparent 70%)"></div>
            <div class="absolute bottom-0 right-0 h-80 w-80" style="background: radial-gradient(closest-side, rgba(255, 174, 0, 0.10), transparent 70%)"></div>
        </div>

        <div class="relative mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:gap-16 lg:px-10">
            <!-- Copy + bullets -->
            <div>
                <h2 class="text-4xl font-black leading-[1.05] tracking-[-0.02em] sm:text-5xl">{{ t('ai.title') }}</h2>
                <p class="mt-5 max-w-[520px] text-lg text-white/70">{{ t('ai.subtitle') }}</p>

                <ul class="mt-9 flex flex-col gap-6">
                    <li v-for="b in bullets" :key="b.title" class="flex gap-4">
                        <span class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-primary ring-1 ring-white/10">
                            <span class="material-symbols-outlined text-[20px]">{{ b.icon }}</span>
                        </span>
                        <div>
                            <h3 class="font-bold">{{ t(b.title) }}</h3>
                            <p class="mt-1 text-sm text-white/60">{{ t(b.text) }}</p>
                        </div>
                    </li>
                </ul>
            </div>

            <!-- Animated AI workflow mock -->
            <div class="relative mx-auto w-full max-w-[460px]">
                <div class="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.06] shadow-2xl">
                    <div class="flex items-center justify-between border-b border-white/10 px-5 py-4">
                        <div class="flex items-center gap-2.5">
                            <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-text-main">
                                <span class="material-symbols-outlined text-[18px]">graph_3</span>
                            </span>
                            <span class="text-sm font-bold">{{ t('ai.mock_title') }}</span>
                        </div>
                        <span class="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-primary">
                            <span class="h-1.5 w-1.5 animate-pulse rounded-full bg-primary"></span>
                            {{ t('ai.mock_live') }}
                        </span>
                    </div>

                    <div class="flex flex-col gap-3 p-5">
                        <!-- Incoming -->
                        <div class="rounded-2xl bg-white/[0.06] px-4 py-3 text-sm text-white/80 ring-1 ring-white/5">
                            <span class="text-[11px] font-semibold uppercase tracking-wide text-white/40">{{ t('ai.mock_input') }}</span>
                            <p class="mt-1 font-medium">“{{ t('hero.mock_prompt') }}”</p>
                        </div>

                        <!-- Steps -->
                        <div
                            v-for="(step, i) in steps"
                            :key="step"
                            class="step flex items-center gap-3 rounded-2xl bg-white/[0.03] px-4 py-3 ring-1 ring-white/5"
                            :style="{ animationDelay: i * 0.5 + 's' }"
                        >
                            <span class="flex h-7 w-7 items-center justify-center rounded-full bg-primary/20 text-primary">
                                <span class="material-symbols-outlined text-[16px]">check</span>
                            </span>
                            <span class="text-sm font-medium text-white/85">{{ t(step) }}</span>
                            <span class="material-symbols-outlined ml-auto text-[18px] text-primary/70">bolt</span>
                        </div>
                    </div>
                </div>

                <!-- accent glow -->
                <div class="pointer-events-none absolute -inset-4 -z-10 rounded-[2rem] bg-primary/20 blur-3xl"></div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

defineProps<{ id?: string }>();

const { t } = useI18n();

const bullets = [
    { icon: 'forum', title: 'ai.b1_title', text: 'ai.b1_text' },
    { icon: 'trending_up', title: 'ai.b2_title', text: 'ai.b2_text' },
    { icon: 'cycle', title: 'ai.b3_title', text: 'ai.b3_text' },
];

const steps = ['ai.mock_step1', 'ai.mock_step2', 'ai.mock_step3'];
</script>

<style scoped>
.step {
    opacity: 0;
    animation: step-in 0.6s ease-out forwards;
}
@keyframes step-in {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
    .step { opacity: 1; animation: none; }
}
</style>
